import React from 'react';

import {
  Box,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import {
  fields,
  Form,
  formUtil
} from 'iron-forge-react-modules';
import {
  useFieldArray,
  useForm
} from 'react-hook-form';

import { appFormUtil } from 'modules/layer00';
import { BaseWidget } from 'modules/layer01';
import * as dataService from './dataService';
import * as types from './types';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();
const ControlledCheckbox = fields.CheckboxField.genControllerComponent<types.IFormState>();



export interface IProps {
  storeId: string;
}

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.storeId);
  const [
    saveCustomOrderFormatFlow,
    saveCustomOrderFormatFlowTuple
  ] = dataService.useSaveCustomOrderFormatFlow(props.storeId);

  const form = useForm<types.IFormState>({
    defaultValues: {
      prefixNewOrders: false,
      mappings: []
    }
  });
  const fieldArray = useFieldArray({
    control: form.control,
    name: 'mappings'
  });

  formUtil.useSetFormFields(queryTuple, form.setValue, ['mappings', 'prefixNewOrders']);



  const processSubmit = React.useCallback(
    (formState: types.IFormState) => {
      saveCustomOrderFormatFlow(appFormUtil.trimFormStateValues(formState), queryTuple.data.flowId);
    },
    [
      queryTuple.data.flowId,
      saveCustomOrderFormatFlow
    ]
  );



  const loading = queryTuple.loading || saveCustomOrderFormatFlowTuple.loading;

  return !queryTuple.data.storeCanDoCsvUploads ? null : (
    <Form.FC
      disableDefaultOverflow
      onSubmit={form.handleSubmit(processSubmit)}>
      <BaseWidget.FC
        title="CSV Format"
        elAction={
          <Button
            disabled={loading}
            variant="contained"
            type="submit">
            Save
          </Button>
        }>
        <Grid
          container
          columnSpacing={1}
          rowSpacing={2}
          alignItems="center">
          <ControlledCheckbox
            label="Prefix Orders on Import"
            formControl={form.control}
            fieldKey="prefixNewOrders" />

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  Property
                </TableCell>
                <TableCell>
                  Column Header
                </TableCell>
                <TableCell>
                  Default Value
                </TableCell>

                <TableCell>
                  Empty Values Allowed?
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {fieldArray.fields.map((field, index) => {
                return (
                  <TableRow key={field.id}>
                    <TableCell>
                      <Box>
                        {field.orderPropertyDisplayName}

                        {field.isRequired && (
                          <Box
                            component="span"
                            sx={{
                              ml: 2,
                              fontSize: 12,
                              color: theme => theme.palette.error.main
                            }}>
                            Required
                          </Box>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <ControlledTextField
                        isRequired={field.isRequired}
                        isDisabled={loading}
                        size="small"
                        formControl={form.control}
                        fieldKey={`mappings.${index}.columnLabel`}
                        disableDefaultMargins />
                    </TableCell>

                    <TableCell>
                      <ControlledTextField
                        isDisabled={loading}
                        size="small"
                        formControl={form.control}
                        fieldKey={`mappings.${index}.defaultValueOnEmpty`}
                        disableDefaultMargins />
                    </TableCell>

                    <TableCell>
                      {!field.isRequired && (
                        <ControlledCheckbox
                          fieldKey={`mappings.${index}.emptyAllowed`}
                          formControl={form.control} />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Grid>
      </BaseWidget.FC>
    </Form.FC>
  );
};
